import type { LoadedPipelineData } from '../../../features/pipeline/pipeline-model.ts';
import type { AppTranslator } from '../../../shared/i18n/browser-translation-contract.ts';
import { isPipelinePresetKey, type PipelinePresetKey } from '../ui/pipeline-presets.ts';
import type {
    LoadPipelineFromFileResult,
    SavePipelineAsFileResult,
} from './pipeline-io-system.ts';

type StatusKind = 'success' | 'error' | 'info';
type StatusReporter = (message: string, kind?: StatusKind) => void;

interface PipelineIoSystemLike {
  savePipelineAsFile: () => Promise<SavePipelineAsFileResult>;
  loadPipelineFromArrayBuffer: (data: ArrayBuffer) => Promise<LoadPipelineFromFileResult>;
  loadPipelineFromFile: (file: File) => Promise<LoadPipelineFromFileResult>;
}

export interface PipelineHeaderActionMountOptions {
  initialCanUndo: boolean;
  initialCanRedo: boolean;
  onUndoPipeline: () => void;
  onRedoPipeline: () => void;
  onResetPresetSelected: (preset: PipelinePresetKey) => void | Promise<void>;
  onSavePipeline: () => void | Promise<void>;
  onPipelineFileSelected: (file: File) => void | Promise<void>;
  onStatus: StatusReporter;
}

export interface PipelineHeaderActionControllerOptions {
  canUndo: () => boolean;
  canRedo: () => boolean;
  onUndoPipeline: () => void;
  onRedoPipeline: () => void;
  onResetPipelineState: () => void;
  onApplyLoadedPipeline: (loaded: LoadedPipelineData) => void;
  getPipelineIoSystem: () => PipelineIoSystemLike | null;
  onStatus: StatusReporter;
  t: AppTranslator;
}

export interface PipelineHeaderActionController {
  buildMountOptions: () => PipelineHeaderActionMountOptions;
  loadPipelineFile: (file: File) => Promise<void>;
  setHistoryHandlers: (handlers: { onUndoPipeline: () => void; onRedoPipeline: () => void }) => void;
  getPipelineHistoryAvailability: () => { canUndo: boolean; canRedo: boolean };
}

function ensureFunction(value: unknown, label: string): void {
  if (typeof value !== 'function') {
    throw new Error(`${label} が不正です。`);
  }
}

function ensureObject(value: unknown, label: string): asserts value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`${label} が不正です。`);
  }
}

function ensureBoolean(value: unknown, label: string): asserts value is boolean {
  if (typeof value !== 'boolean') {
    throw new Error(`${label} が不正です。`);
  }
}

function hasFileApi(): boolean {
  return typeof File !== 'undefined';
}

function isFile(value: unknown): value is File {
  return hasFileApi() && value instanceof File;
}

function ensureOptions(value: unknown): asserts value is PipelineHeaderActionControllerOptions {
  if (!value || typeof value !== 'object') {
    throw new Error('PipelineHeaderActionController の options が不正です。');
  }

  const options = value as Partial<PipelineHeaderActionControllerOptions>;
  ensureFunction(options.canUndo, 'PipelineHeaderActionController: canUndo');
  ensureFunction(options.canRedo, 'PipelineHeaderActionController: canRedo');
  ensureFunction(options.onUndoPipeline, 'PipelineHeaderActionController: onUndoPipeline');
  ensureFunction(options.onRedoPipeline, 'PipelineHeaderActionController: onRedoPipeline');
  ensureFunction(options.onResetPipelineState, 'PipelineHeaderActionController: onResetPipelineState');
  ensureFunction(options.onApplyLoadedPipeline, 'PipelineHeaderActionController: onApplyLoadedPipeline');
  ensureFunction(options.getPipelineIoSystem, 'PipelineHeaderActionController: getPipelineIoSystem');
  ensureFunction(options.onStatus, 'PipelineHeaderActionController: onStatus');
  ensureFunction(options.t, 'PipelineHeaderActionController: t');
}

export function createPipelineHeaderActionController(
  options: PipelineHeaderActionControllerOptions,
): PipelineHeaderActionController {
  ensureOptions(options);

  let onUndoPipelineHandler = options.onUndoPipeline;
  let onRedoPipelineHandler = options.onRedoPipeline;

  const onUndoPipeline = (): void => {
    onUndoPipelineHandler();
  };

  const onRedoPipeline = (): void => {
    onRedoPipelineHandler();
  };

  const onResetPresetSelected = async (preset: PipelinePresetKey): Promise<void> => {
    if (!isPipelinePresetKey(preset)) {
      options.onStatus(
        options.t('main.status.pipelineLoadFailed', {
          message: `不正なプリセットです: ${String(preset)}`,
        }),
        'error',
      );
      return;
    }

    if (preset === 'Initial') {
      options.onResetPipelineState();
      options.onStatus(options.t('main.status.resetStepChain'), 'info');
      return;
    }

    const pipelineIoSystem = options.getPipelineIoSystem();
    if (!pipelineIoSystem) {
      options.onStatus(options.t('main.status.pipelineIoNotInitialized'), 'error');
      return;
    }

    const response = await fetch(new URL(`examples/${preset}.lutchain`, window.location.href));
    if (!response.ok) {
      options.onStatus(
        options.t('main.status.pipelineLoadFailed', {
          message: `examples/${preset}.lutchain の取得に失敗しました (${response.status})`,
        }),
        'error',
      );
      return;
    }

    const result = await pipelineIoSystem.loadPipelineFromArrayBuffer(await response.arrayBuffer());
    if (!result.ok || !result.loaded) {
      options.onStatus(
        options.t('main.status.pipelineLoadFailed', {
          message: result.errorMessage ?? options.t('common.unknownError'),
        }),
        'error',
      );
      return;
    }

    options.onApplyLoadedPipeline(result.loaded);
  };

  const onSavePipeline = async (): Promise<void> => {
    const pipelineIoSystem = options.getPipelineIoSystem();
    if (!pipelineIoSystem) {
      options.onStatus(options.t('main.status.pipelineIoNotInitialized'), 'error');
      return;
    }

    const result = await pipelineIoSystem.savePipelineAsFile();
    if (result.ok) {
      options.onStatus(options.t('main.status.pipelineSaved'), 'success');
      return;
    }

    options.onStatus(
      options.t('main.status.pipelineSaveFailed', {
        message: result.errorMessage ?? options.t('common.unknownError'),
      }),
      'error',
    );
  };

  const onPipelineFileSelected = async (file: File): Promise<void> => {
    if (!isFile(file)) {
      options.onStatus(
        options.t('main.status.pipelineLoadFailed', {
          message: '入力ファイルが不正です。',
        }),
        'error',
      );
      return;
    }

    const pipelineIoSystem = options.getPipelineIoSystem();
    if (!pipelineIoSystem) {
      options.onStatus(options.t('main.status.pipelineIoNotInitialized'), 'error');
      return;
    }

    const result = await pipelineIoSystem.loadPipelineFromFile(file);
    if (!result.ok || !result.loaded) {
      options.onStatus(
        options.t('main.status.pipelineLoadFailed', {
          message: result.errorMessage ?? options.t('common.unknownError'),
        }),
        'error',
      );
      return;
    }

    options.onApplyLoadedPipeline(result.loaded);
  };

  const buildMountOptions = (): PipelineHeaderActionMountOptions => {
    const initialCanUndo = options.canUndo();
    const initialCanRedo = options.canRedo();

    ensureBoolean(initialCanUndo, 'PipelineHeaderActionController: initialCanUndo');
    ensureBoolean(initialCanRedo, 'PipelineHeaderActionController: initialCanRedo');

    return {
      initialCanUndo,
      initialCanRedo,
      onUndoPipeline,
      onRedoPipeline,
      onResetPresetSelected,
      onSavePipeline,
      onPipelineFileSelected,
      onStatus: options.onStatus,
    };
  };

  return {
    buildMountOptions,
    loadPipelineFile: onPipelineFileSelected,
    setHistoryHandlers: handlers => {
      ensureObject(handlers, 'PipelineHeaderActionController: handlers');
      ensureFunction(handlers.onUndoPipeline, 'PipelineHeaderActionController: handlers.onUndoPipeline');
      ensureFunction(handlers.onRedoPipeline, 'PipelineHeaderActionController: handlers.onRedoPipeline');
      onUndoPipelineHandler = handlers.onUndoPipeline;
      onRedoPipelineHandler = handlers.onRedoPipeline;
    },
    getPipelineHistoryAvailability: () => ({
      canUndo: options.canUndo(),
      canRedo: options.canRedo(),
    }),
  };
}
